'use client'

import { useEffect, useRef, useState } from 'react'
import { SetFlowChart, SetFlowChartBars } from '@/components/portal/SetFlowChart'
import { SetNarrative } from './LiveNarrative'
import { buildLiveSetFlows } from '@/lib/live/set-flow'
import { buildLiveNarrative, type NarrativeEventRow } from '@/lib/live/narrative'
import type { MatchState, MatchStatus } from '@/lib/live/state'

/** Stati in cui la partita è in campo: finché dura, il pezzo live si aggiorna. */
const IN_CAMPO: MatchStatus[] = ['live', 'pause', 'timeout_home', 'timeout_away']

/**
 * Il pezzo live del match center: andamento dei set e cronaca che **crescono
 * durante la partita**.
 *
 * Il match center è un server component: senza questo, i dati restavano quelli del
 * render e il racconto arrivava solo ricaricando — cioè, di fatto, a partita finita
 * (quando ormai la partita è uscita dal live center). Qui ci si aggiorna **a
 * intervalli**, con due richieste minuscole per giro (stato + eventuali eventi
 * nuovi) e **senza aprire il canale WebSocket**: la pagina della partita è pubblica
 * e può avere molti lettori, un socket a testa non è il modo di servirla.
 *
 * I dati del primo paint arrivano dal server (stessa forma della pagina `/live`),
 * quindi il contenuto c'è anche senza JavaScript.
 */
export function LiveMatchFeed({
  sessionKey,
  initialStatus,
  initialRevision,
  initialSetHistory,
  initialEvents,
  homeTeam,
  awayTeam,
  homeColor,
  awayColor,
  showAndamento,
  intervalloMs = 15000,
}: {
  sessionKey: string
  initialStatus: MatchStatus
  initialRevision: number
  initialSetHistory: Array<{ home: number; away: number }>
  initialEvents: NarrativeEventRow[]
  homeTeam: string
  awayTeam: string
  homeColor?: string
  awayColor?: string
  /** L'andamento dal referto c'è già: qui resta solo la cronaca. */
  showAndamento: boolean
  intervalloMs?: number
}) {
  const [status, setStatus] = useState<MatchStatus>(initialStatus)
  const [setHistory, setSetHistory] = useState(initialSetHistory)
  const [events, setEvents] = useState(initialEvents)
  const revisionRef = useRef(initialRevision)
  const lastIdRef = useRef(initialEvents.length > 0 ? initialEvents[initialEvents.length - 1].id : 0)

  useEffect(() => {
    let cancelled = false
    let timer: ReturnType<typeof setInterval> | null = null
    const ferma = () => {
      if (timer) {
        clearInterval(timer)
        timer = null
      }
    }

    const giro = async () => {
      try {
        // Lo stato dice se la partita è ancora in campo, a che set siamo e i
        // parziali dei set chiusi (servono a dire chi li ha vinti). Si chiede
        // **subito**, anche se lo stato servito diceva «finita»: la pagina può
        // essere stata resa qualche istante prima, e un giro solo costa niente.
        const r = await fetch(`/api/live/${sessionKey}/state`, { cache: 'no-store' })
        // 404 = la sessione non c'è più (cancellata) o non è più leggibile
        // (`visible:false` per un anonimo): è **terminale**, non transitorio.
        // Va fermato il timer, altrimenti una gara nascosta mentre la pagina è
        // aperta resta un poller infinito — una richiesta ogni intervallo per
        // ogni lettore del match center.
        if (r.status === 404) {
          ferma()
          return
        }
        // 5xx, timeout, rete giù: transitori, si riprova al giro dopo.
        if (!r.ok) return
        const j = (await r.json()) as {
          session?: { revision: number; status: MatchStatus; state: MatchState }
        }
        const s = j.session
        if (cancelled || !s) return
        setStatus(s.status)
        if (s.state?.setHistory) setSetHistory(s.state.setHistory)

        // Partita finita: si smette di chiedere. Altrimenti si tiene il passo.
        if (!IN_CAMPO.includes(s.status)) {
          ferma()
          return
        }
        if (!timer) timer = setInterval(giro, intervalloMs)

        // Gli eventi si chiedono **solo** se è cambiata la revisione, e solo quelli
        // nuovi: poche righe, non tutto lo storico.
        if (s.revision === revisionRef.current) return
        revisionRef.current = s.revision
        const e = await fetch(
          `/api/live/${sessionKey}/events?after=${lastIdRef.current}&limit=100`,
          { cache: 'no-store' },
        )
        // Stessa porta del `/state`: la sessione può sparire **fra** le due
        // chiamate (nascosta dopo il 200 dello stato). Senza `ferma()` resta
        // armato un tick in più, che qui non serve a niente.
        if (e.status === 404) {
          ferma()
          return
        }
        if (!e.ok) return
        const batch = ((await e.json()) as { events: NarrativeEventRow[] }).events ?? []
        if (cancelled || batch.length === 0) return
        setEvents((prev) => {
          const known = new Set(prev.map((x) => x.id))
          const merged = [...prev, ...batch.filter((x) => !known.has(x.id))].sort((x, y) => x.id - y.id)
          lastIdRef.current = merged[merged.length - 1]?.id ?? lastIdRef.current
          return merged
        })
      } catch {
        // Rete giù: si riprova al giro dopo (o si smette, se non c'è un giro dopo).
        if (!timer) timer = setInterval(giro, intervalloMs)
      }
    }

    void giro()
    return () => {
      cancelled = true
      ferma()
    }
  }, [sessionKey, intervalloMs])

  const flows = showAndamento ? buildLiveSetFlows(events, setHistory) : []
  const cronaca = buildLiveNarrative(events, setHistory)

  const cronacaPerSet = new Map(cronaca.map((set) => [set.set, set]))

  // Col referto già disponibile la cronaca la colloca il match center, dentro la
  // box di ogni set: qui non resta niente da mostrare.
  if (flows.length === 0) return null

  return (
    <section className="mt-8">
      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
        <p className="league-kicker">Andamento dei set</p>
        <span className="text-[12px] text-[color:var(--league-muted)]">
          {IN_CAMPO.includes(status)
            ? 'Punti dal live, uno per scambio — si aggiorna da sé'
            : 'Punti dal live — in attesa del referto ufficiale'}
        </span>
      </div>
      <div className="mt-4 space-y-4">
        {flows.map((flow, index) => (
          <div key={flow.set} className="league-card rounded-2xl p-4 md:p-5">
            <p className="text-[14px] font-semibold text-[color:var(--league-ink)]">Set {flow.set}</p>
            <div className="mt-3 hidden md:block" data-grafico-set="h">
              <SetFlowChart
                flow={flow}
                home={homeTeam}
                away={awayTeam}
                homeColor={homeColor}
                awayColor={awayColor}
                idPrefix={`setflow-live-h-${flow.set}`}
              />
            </div>
            <div className="md:hidden" data-grafico-set="bars">
              <SetFlowChartBars flow={flow} home={homeTeam} away={awayTeam} />
            </div>
            {/* La cronaca del set, sotto il grafico: lo stesso set, raccontato azione
                per azione. Il più recente (l'ultimo dell'elenco) resta aperto. */}
            <SetNarrative
              set={cronacaPerSet.get(flow.set)}
              homeTeam={homeTeam}
              awayTeam={awayTeam}
              titolo="Cronaca del set"
              homeColor={homeColor}
              awayColor={awayColor}
              open={index === flows.length - 1}
              mostraPunteggio={false}
              className="mt-4 border-t border-[color:var(--league-line)] pt-3"
            />
          </div>
        ))}
      </div>
    </section>
  )
}
